interface ISalesforceOrderRequestParams {
    accountId: number;
    eventId: number;
    orderId?: string;
    signal?: AbortSignal;
}

interface ISalesforceLookupDTO {
    id: string;
    name: string;
}
interface ISalesforceAddressDTO {
    street: string | null;
    city: string | null;
    state: string | null;
    postalCode: string | null;
    country: string | null;
}

interface ISalesforceOrderProductDTO {
    lineItemId: string | null;
    productName: string;
    currency: string | null;
    listPrice: string | number | null;
    unitPrice: string | number | null;
    notes: string | null;
    quantity: string | number | null;
    total: string | number | null;
}

interface ISalesforceOrderRecordDTO {
    orderStatus: string | null;
    billingStatus: string | null;
    contractNumber: ISalesforceLookupDTO | string | null;
    multiEventOrder: string | null;
    parentOrder: ISalesforceLookupDTO | null;
    requesterContact: ISalesforceLookupDTO | null;
    corporateRelated: ISalesforceLookupDTO | null;
    businessLead?: ISalesforceLookupDTO | null;
    // Despite the "Name" suffix, this is the full {id, name} lookup object (like
    // `channelAgencyReferral` below), not a display string — kept as `opportunityName` because it
    // mirrors the API response shape directly (no remapping step; see `response.parsedResponse`
    // in implementations/salesforce-order.dao.ts). Compare `IOrderTemplateValues.opportunityName`
    // (order.template.types.ts), which IS a plain string.
    opportunityName?: ISalesforceLookupDTO | null;
    channelAgencyReferral?: ISalesforceLookupDTO | null;
    hostLob?: string | null;
    hostSubLob?: string | null;
    feedbackFormUrl?: string | null;
    orderType: string | null;
    orderSubtype: string | null;
    eventType?: string | null;
    eventTypeClient?: string | null;
    pricebook: ISalesforceLookupDTO | null;
    currencyIsoCode: string | null;
    cancellationReason: string | null;
    cancellationOther: string | null;
    cancellationFee: string | null;
    syncToTeams: boolean | null;
    noStaffRequired: boolean | null;
    hybridEvent: string | null;
    recordType?: ISalesforceLookupDTO | string | null;

    highProfileOrder?: boolean | null;
    highProfileReasoning?: string | null;
    highProfileAttendeeDetails?: string | null;
    accountLegalEntity?: string | null;
    isThisEventRepeatable?: string | null;
    lassoSyncStatus?: string | null;
    lassoSyncDatetime?: string | null;
    teamsCreated?: string | null;
    processedForArchive?: boolean | null;
    billingTimeSensitivity?: string | null;

    billToLegalEntity: ISalesforceLookupDTO | null;
    billingContact: ISalesforceLookupDTO | null;
    billingContactEmail: string | null;
    billingAddress: ISalesforceAddressDTO | null;
    additionalRecipient1: ISalesforceLookupDTO | null;
    additionalRecipient1Email: string | null;
    additionalRecipient2: ISalesforceLookupDTO | null;
    additionalRecipient2Email: string | null;
    billingMethod: string | null;
    billingConfirmation: boolean | null;
    clientCostCode: string | null;
    secondaryPO: string | null;
    billingNotes: string | null;

    eventRanSmoothly: boolean | null;
    postEventNotes: string | null;
    qaChatEnabled: string | null;
    qaReportSent: string | null;

    description: string | null;
    oecEventId?: string | null;
    oeCentralUrl?: string | null;
    oecUrl?: string | null;
}

interface ISalesforceOrderDTO {
    orderId: string | null;
    available: boolean;
    orderLinked: boolean;
    eventName?: string | null;
    eventType?: string | null;
    oecEventId?: string | null;
    oeCentralUrl?: string | null;
    oecUrl?: string | null;
    order: ISalesforceOrderRecordDTO | null;
    products: ISalesforceOrderProductDTO[];
    message: string | null;
}

interface ISalesforceOrderCreateProduct {
    pricebookEntryId: string;
    // The OrderItem being updated. Omitted entirely for a line the PM has just added — the OEC/OEI contract marks
    // this field @JsonInclude(NON_NULL) and reads an absent id as "insert this line" (OEP-38900).
    lineItemId?: string;
    quantity: number;
    unitPrice: number | null;
    notes: string;
}

interface ISalesforceOrderCreateEvent {
    orderId: string;
    accountId: string;
    contractId?: string;
    recordTypeName?: string;
    name: string;
    startDateTime: string;
    endDateTime: string;
    timeZone: string;
    region: string;
    eventType: string;
    department: string;
    oecEventId: string;
    externalEventId: string;
    oecUrl: string;
}

interface ISalesforceOrderCreatePayload extends ISalesforceOrderCreateEvent {
    orderStatus: string;
    billingStatus: string;
    multiEventOrder: string;
    parentOrder: string;
    requesterContact: string;
    corporateRelated: string;
    opportunityId: string;
    channelAgencyReferral: string;
    hostLob: string;
    hostSubLob: string;
    billToLegalEntity: string;
    orderType: string;
    orderSubtype: string;
    cancellationFee: string;
    cancellationReason: string;
    cancellationOther: string;
    syncToTeams: boolean;
    noStaffRequired: boolean;
    hybridEvent: string;
    eventTypeClient: string;
    highProfileOrder: boolean;
    highProfileReasoning: string;
    highProfileAttendeeDetails: string;
    accountLegalEntity: string;
    isThisEventRepeatable: string;
    billingTimeSensitivity: string;
    billingContact: string;
    additionalRecipient1: string;
    additionalRecipient2: string;
    billingMethod: string;
    clientCostCode: string;
    secondaryPO: string;
    billingNotes: string;
    qaChatEnabled: string;
    qaReportSent: string;
    eventRanSmoothly: boolean;
    postEventNotes: string;
    description: string;
    pricebookId: string | null;
    currencyIsoCode: string;
    products: ISalesforceOrderCreateProduct[];
    /** Salesforce user id. Sent on edit only. Omitted on create and clone so Salesforce assigns the Owner. */
    businessLead?: string;
}

interface ISalesforceOrderCreateParams {
    accountId: number;
    eventId: number;
    payload: ISalesforceOrderCreatePayload;
}

interface ISalesforceOrderCreateResult {
    success: boolean;
    orderId: string | null;
    orderNumber?: string | null;
    errorMessage?: string | null;
    warningMessage?: string | null;
}

type ISalesforceOrderUpdatePayload = ISalesforceOrderCreatePayload;

interface ISalesforceOrderUpdateParams {
    accountId: number;
    eventId: number;
    orderId: string;
    payload: ISalesforceOrderUpdatePayload;
}


interface ISalesforceOrderFormDataRequestParams {
    accountId: number;
    eventId?: number;
}

interface ISalesforcePricebookDTO {
    id: string;
    name: string;
    businessLine: string | null;
}

interface ISalesforceMultiEventOrderDTO {
    id: string;
    name: string;
    orderNumber: string;
    status: string;
    pricebook: ISalesforcePricebookDTO | null;
}

interface ISalesforceContractDTO {
    id: string;
    name: string;
    contractNumber: string;
    startDate: string;
    endDate: string;
    businessLine: string | null;
    pricebook: ISalesforcePricebookDTO | null;
    currencyIsoCode?: string | null;
}

interface ISalesforceContactDTO {
    id: string;
    firstName: string;
    lastName: string;
    name: string;
    email: string;
    mailingAddress: ISalesforceAddressDTO | null;
}

interface IRecentOrderDTO {
    id: string;
    name: string;
    orderNumber: string;
    startDate: string;
    endDate: string;
    eventType?: string | null;
    requesterName?: string | null;
    oecEventId?: string | null;
    oeCentralUrl?: string | null;
}

interface ISalesforceOrderFormDataDTO {
    available: boolean;
    accountLinked: boolean;
    accountId: string;
    accountName: string;
    pricebooks: ISalesforcePricebookDTO[];
    multiEventOrders: ISalesforceMultiEventOrderDTO[];
    contracts: ISalesforceContractDTO[];
    contacts: ISalesforceContactDTO[];
    recentOrders: IRecentOrderDTO[];
    eventType?: string[];
    message: string | null;
}

interface ISalesforcePriceBookProductDTO {
    pricebookEntryId: string;
    productName: string;
    productFamily: string | null;
    priceBookEntryDescription: string | null;
    listPrice: number | null;
    currencyIsoCode: string;
}

interface ISalesforcePriceBookProductsParams {
    priceBookId: string;
    contractId?: string | null;
}

interface ISalesforcePriceBookProductsDTO {
    available: boolean;
    products: ISalesforcePriceBookProductDTO[];
    message: string | null;
}

interface ISalesforceAccountSearchParams {
    accountId: number;
    searchTerm: string;
}

interface ISalesforceAccountDTO {
    id: string;
    name: string;
}

interface ISalesforceAccountSearchDTO {
    available: boolean;
    searchTerm: string;
    accounts: ISalesforceAccountDTO[];
    message: string | null;
}

interface ISalesforceUserSearchParams {
    accountId: number;
    searchTerm: string;
}

interface ISalesforceUserDTO {
    id: string;
    name: string;
}

interface ISalesforceUserSearchDTO {
    available: boolean;
    searchTerm: string;
    businessLeads: ISalesforceUserDTO[];
    message: string | null;
}

interface ISalesforceOpportunityDTO {
    id: string;
    name: string;
    accountName?: string | null;
    stageName?: string | null;
    opportunityNumber?: string | null;
    currencyIsoCode?: string | null;
    closeDate?: string | null;
}

interface ISalesforceOpportunitySearchParams {
    accountId: number;
    searchTerm: string;
}

interface ISalesforceOpportunitySearchDTO {
    available?: boolean;
    searchTerm?: string;
    opportunities?: ISalesforceOpportunityDTO[];
    message?: string | null;
}

interface IRecentOrderSearchParams {
    accountId: number;
    searchTerm: string;
    signal?: AbortSignal;
}

interface IRecentOrderSearchDTO {
    available: boolean;
    searchTerm: string;
    recentOrders: IRecentOrderDTO[];
    message: string | null;
}

interface ISalesforceOrderLinkParams {
    accountId: number;
    eventId: number;
    orderId: string;
    orderNumber: string;
}

interface ISalesforceOrderLinkPayload {
    orderId: string;
    oeNumber: string;
}

abstract class ISalesforceOrderDAO {
    public abstract getOrderForEvent(params: ISalesforceOrderRequestParams): Promise<ISalesforceOrderDTO>;
    public abstract getOrderFormData(params: ISalesforceOrderFormDataRequestParams): Promise<ISalesforceOrderFormDataDTO>;
    // Search Salesforce Accounts for Corporate Related and Bill To (Legal Entity) lookups.
    public abstract searchAccounts(params: ISalesforceAccountSearchParams): Promise<ISalesforceAccountDTO[]>;
    public abstract searchUsers(params: ISalesforceUserSearchParams): Promise<ISalesforceUserDTO[]>;
    public abstract searchOpportunities(params: ISalesforceOpportunitySearchParams): Promise<ISalesforceOpportunityDTO[]>;
    public abstract searchRecentOrders(params: IRecentOrderSearchParams): Promise<IRecentOrderDTO[]>;
    public abstract createOrder(params: ISalesforceOrderCreateParams): Promise<ISalesforceOrderCreateResult>;
    public abstract updateOrder(params: ISalesforceOrderUpdateParams): Promise<ISalesforceOrderCreateResult>;
    public abstract linkOrder(params: ISalesforceOrderLinkParams): Promise<ISalesforceOrderCreateResult>;
    // Product catalogue for a Price Book (all currencies) — powers the Manage Products modal.
    public abstract getPriceBookProducts(params: ISalesforcePriceBookProductsParams): Promise<ISalesforcePriceBookProductsDTO>;
}

export {
    ISalesforceOrderDAO,
    ISalesforceOrderDTO,
    ISalesforceOrderRecordDTO,
    ISalesforceOrderProductDTO,
    ISalesforceLookupDTO,
    ISalesforceAddressDTO,
    ISalesforceOrderRequestParams,
    ISalesforceOrderCreateParams,
    ISalesforceOrderCreateEvent,
    ISalesforceOrderCreateProduct,
    ISalesforceOrderCreatePayload,
    ISalesforceOrderCreateResult,
    ISalesforceOrderUpdatePayload,
    ISalesforceOrderUpdateParams,
    ISalesforceOrderFormDataRequestParams,
    ISalesforceOrderFormDataDTO,
    ISalesforcePricebookDTO,
    ISalesforceMultiEventOrderDTO,
    ISalesforceContractDTO,
    ISalesforceContactDTO,
    ISalesforceAccountSearchParams,
    ISalesforceAccountDTO,
    ISalesforceAccountSearchDTO,
    ISalesforceUserSearchParams,
    ISalesforceUserDTO,
    ISalesforceUserSearchDTO,
    ISalesforceOpportunityDTO,
    ISalesforceOpportunitySearchParams,
    ISalesforceOpportunitySearchDTO,
    IRecentOrderDTO,
    IRecentOrderSearchParams,
    IRecentOrderSearchDTO,
    ISalesforceOrderLinkParams,
    ISalesforceOrderLinkPayload,
    ISalesforcePriceBookProductDTO,
    ISalesforcePriceBookProductsParams,
    ISalesforcePriceBookProductsDTO,
};
